import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { CATEGORY_LABELS } from "@/lib/supportHelpers";

const SUBJECT_MAX = 200;
const BODY_MAX = 4000;

export default function SupportNewPage() {
    const navigate = useNavigate();
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [category, setCategory] = useState("general");
    const [submitting, setSubmitting] = useState(false);
    useDocumentTitle("Raise a ticket");

    const canSubmit = subject.trim().length > 0
        && body.trim().length > 0
        && !submitting;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/support/tickets", {
                subject: subject.trim(), body: body.trim(), category,
            });
            toast.success(`Ticket ${data.ticket ? `HL-${String(data.ticket.ticket_number).padStart(3,"0")}` : ""} raised.`);
            navigate(`/support/${data.ticket.id}`);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Could not raise ticket");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="mx-auto max-w-2xl space-y-6 animate-fade-in-up" data-testid="support-new-page">
                <Button asChild variant="ghost" size="sm" className="-ml-2 text-muted-foreground" data-testid="support-new-back">
                    <Link to="/support"><ArrowLeft className="h-4 w-4" /> Your tickets</Link>
                </Button>

                <header className="space-y-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">Support</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">Raise a ticket</h1>
                    <p className="text-sm text-muted-foreground">
                        Describe what&apos;s happening. We typically reply within a working day.
                    </p>
                </header>

                <Card className="border-border/80 shadow-sm">
                    <CardContent className="p-6">
                        <form onSubmit={onSubmit} className="space-y-5">
                            <div className="space-y-1.5">
                                <Label htmlFor="subject">Subject</Label>
                                <Input
                                    id="subject"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value.slice(0, SUBJECT_MAX))}
                                    placeholder="What's the issue, in one line?"
                                    required
                                    data-testid="support-new-subject"
                                />
                                <p className="text-right text-xs text-muted-foreground tabular-nums">{subject.length}/{SUBJECT_MAX}</p>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="category">Category</Label>
                                <Select value={category} onValueChange={setCategory}>
                                    <SelectTrigger id="category" data-testid="support-new-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                                            <SelectItem key={v} value={v}>{l}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="body">Message</Label>
                                <Textarea
                                    id="body"
                                    value={body}
                                    onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
                                    placeholder="Share what's happening, what you expected, and any steps to reproduce."
                                    rows={8}
                                    className="min-h-32"
                                    required
                                    data-testid="support-new-body"
                                />
                                <p className="text-right text-xs text-muted-foreground tabular-nums">{body.length}/{BODY_MAX}</p>
                            </div>

                            <div className="flex justify-end gap-2 pt-2">
                                <Button type="button" variant="ghost" onClick={() => navigate("/support")}>Cancel</Button>
                                <Button
                                    type="submit"
                                    disabled={!canSubmit}
                                    className="bg-primary hover:bg-primary-hover font-semibold"
                                    data-testid="support-new-submit"
                                >
                                    {submitting ? "Submitting…" : "Submit ticket"}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
